import { Play as PlayArrow } from '@tunasong/icons'
import { useAudioEngine, type MidiNoteEvent } from '@tunasong/audio-ui'
import { logger } from '@tunasong/models'
import type { Chord, ChordVariant, NoteEvent, NoteOctave } from '@tunasong/music-lib'
import { ChordLib, getNoteAndOctave, GuitarLib, NoteLib, ScaleLib, symbols } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import { Divider, Typography, useDimensions, VBox, Grid, IconButton, Paper, Button, Tabs, Tab } from '@tunasong/ui-lib'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Fretboard } from '../guitar/visualization/fretboard'
import { GuitarChord } from '../guitar/visualization/guitar-chord'
import { Keyboard } from '../keyboard/keyboard'
import { Note } from '../note/note'
import { usePlayNotes } from '../player/piano-player.hook.js'
import CircleOfFifths from '../scale/circle-of-fifths.js'
import styles from './theory.module.css'

interface MusicTheoryProps {
  scale: Scale
}

export const MusicTheory = ({ scale }: MusicTheoryProps) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedChord, setSelectedChord] = useState<ChordVariant | null>(null)

  const { playNotes, playNoteSequence } = usePlayNotes()

  // Get the audio mixer for playing sounds
  const { isSmallOrSmaller } = useDimensions()

  // Generate fretboard fingerings based on scale
  const fingeringsFromScale = scale ? GuitarLib.getFingeringsForScale({ scale }) : []

  // Get scale notes
  const scaleNotes = scale ? ScaleLib.getNotesWithOctave(scale, 4) : []

  // Get scale chords
  const scaleChords = scale ? ScaleLib.getChords(scale) : []

  const [activeNotes, setActiveNotes] = useState<NoteOctave[]>([])

  const engine = useAudioEngine()
  useEffect(() => {
    if (!engine) {
      return
    }
    const handlePlay = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return
      }
      const noteEvent = event.detail as MidiNoteEvent
      const { note: noteSpec, type } = noteEvent

      const note = `${noteSpec.name}${noteSpec.accidental ? noteSpec.accidental : ''}${noteSpec.octave}` as NoteOctave

      logger.debug('Received note event:', noteEvent, note)

      if (type === 'noteon') {
        setActiveNotes(prev => [...new Set([...prev, note])])
      } else if (type === 'noteoff') {
        setActiveNotes(prev => prev.filter(n => n !== note))
      }
    }
    engine.addEventListener('midievent', handlePlay)
    return () => {
      engine.removeEventListener('midievent', handlePlay)
    }
  }, [engine])

  // Create activeNotes for keyboard based on selected chord or scale
  const chordNotes = useMemo(() => {
    if (selectedChord) {
      const chordNotes = ChordLib.getNotes(selectedChord)
      const octaves = [3, 4, 5]
      return octaves.flatMap(octave => chordNotes.map(note => `${note}${octave}` as NoteOctave))
    } if (scale) {
      const notes = ScaleLib.getNotes(scale)
      const octaves = [3, 4, 5]
      return octaves.flatMap(octave => notes.slice(0, 7).map(note => `${note}${octave}` as NoteOctave))
    }
    return []
  }, [scale, selectedChord])

  // Generate guitar fingerings for selected chord
  const chordFingerings = useMemo(
    () => (selectedChord ? GuitarLib.getFingeringWithMetadata(GuitarLib.getFingering(selectedChord) ?? [], scale) : []),
    [selectedChord, scale]
  )

  // Play a chord using the audio engine
  const playChord = useCallback(
    (chord: Chord) => {
      const notes: NoteEvent[] = ChordLib.getNotes(chord).map(n => ({
        type: 'note',
        note: `${n}4` as NoteName,
        duration: '4n',
      }))
      playNotes(notes)
    },
    [playNotes]
  )

  // Play a scale using the audio engine
  const playScale = async () => {
    if (!scale) {
      return
    }

    const notes = ScaleLib.getNotesWithOctave(scale, 4)
    const noteEvents: NoteEvent[] = notes.filter(Boolean).map(noteEvent => {
      const { note, octave } = getNoteAndOctave(noteEvent)
      return {
        type: 'note',
        note,
        octave,
        duration: '4n',
      } satisfies NoteEvent
    })

    playNoteSequence(noteEvents)
  }

  // Handle tab changes
  const handleTabChange = (_ev: React.SyntheticEvent | null, newValue: string | number) => {
    setSelectedTab(newValue as number)
  }

  // Handle chord selection
  const handleChordClick = (chord: ChordVariant) => {
    setSelectedChord(chord === selectedChord ? null : chord)
    playChord(chord)
  }

  // Clear selected chord and return to scale view
  const handleClearChord = () => {
    setSelectedChord(null)
  }

  const showNotes = activeNotes.length > 0 ? activeNotes : chordNotes.length > 0 ? chordNotes : scaleNotes

  return (
    <div>
      {scale && (
        <>
          <Grid container spacing={2} className={styles.gridSpacing}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body1">
                {ScaleLib.getName(scale)}
                <IconButton color="primary" onClick={playScale} size="small" className={styles.playScaleButton} title="Play scale">
                  <PlayArrow />
                </IconButton>
              </Typography>

              <Typography variant="body2" gutterBottom>
                <strong>Notes:</strong> {scaleNotes.map(note => NoteLib.prettifyNote(note)).join(' - ')}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Scale Intervals & notes
              </Typography>
              {/* Scale intervals */}
              <Grid container spacing={1} className={styles.intervalGrid}>
                {scaleNotes.slice(0, 7).map((note, index) => (
                  <Grid key={index}>
                    <Paper elevation={1} className={styles.intervalCell}>
                      <Note note={note} scale={scale} />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="subtitle1" gutterBottom>
                Chords in {scale.root} {scale.type} (click to view fingerings)
              </Typography>

              <Grid container spacing={2} className={styles.chordGrid}>
                {scaleChords.map((chord, index) => (
                  <Grid key={index}>
                    <div className={styles.chordButtonWrapper}>
                      <Button
                        variant={chord === selectedChord ? 'contained' : 'outlined'}
                        onClick={() => handleChordClick(chord)}
                        className={styles.chordButton}
                      >
                        <VBox>
                          <Typography variant="caption">
                            {ScaleLib.prettyScaleChordDegree(scale, ScaleLib.SCALE_DEGREE_NAMES[index])}
                          </Typography>
                          <Typography>
                            {NoteLib.prettifyNote(chord.root)}
                            {symbols.prettyPrint(chord.variant)}
                          </Typography>
                        </VBox>
                      </Button>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} className={styles.circleOfFifthsGrid}>
              <div className={styles.circleOfFifthsContainer}>
                <CircleOfFifths scale={scale} />
              </div>
            </Grid>
          </Grid>

          <Divider className={styles.divider} />

          {selectedChord && (
            <div className={styles.selectedChordSection}>
              <div className={styles.flexCenter}>
                <Typography variant="h6">
                  Selected Chord: {NoteLib.prettifyNote(selectedChord.root)}
                  {selectedChord.variant}
                </Typography>
                <IconButton
                  color="primary"
                  onClick={() => playChord(selectedChord)}
                  size="small"
                  className={styles.playChordButton}
                  title="Play chord"
                >
                  <PlayArrow />
                </IconButton>
                <Button variant="text" color="inherit" onClick={handleClearChord} size="small" className={styles.backToScaleButton}>
                  (Back to Scale View)
                </Button>
              </div>
              <Typography variant="body2" color="text.secondary">
                Notes:{' '}
                {ChordLib.getNotes(selectedChord)
                  .map(note => NoteLib.prettifyNote(note))
                  .join(', ')}
              </Typography>
              {chordFingerings.length > 0 && <GuitarChord chord={selectedChord} scale={scale} />}
            </div>
          )}

          <Divider className={styles.divider} />

          <Tabs value={selectedTab} onChange={handleTabChange} className={styles.tabsSection}>
            <Tab label="Guitar" />
            <Tab label="Keyboard" />
          </Tabs>

          <div className={styles.tabContent}>
            {selectedTab === 0 && (
              <div>
                <div className={styles.flexCenter}>
                  <Typography variant="subtitle1" gutterBottom>
                    {selectedChord
                      ? `${NoteLib.prettifyNote(selectedChord.root)}${selectedChord.variant} Chord`
                      : `${scale.root} ${scale.type}`}{' '}
                  </Typography>
                </div>

                <Fretboard
                  scale={scale}
                  fingerings={selectedChord ? chordFingerings : fingeringsFromScale}
                  labels="note"
                />
              </div>
            )}

            {selectedTab === 1 && (
              <VBox>
                <div className={styles.flexCenter}>
                  <Typography variant="h6">
                    {selectedChord
                      ? `${NoteLib.prettifyNote(selectedChord.root)}${selectedChord.variant} Chord`
                      : `${scale.root} ${scale.type} Scale`}{' '}
                    on Keyboard
                  </Typography>
                </div>
                <Keyboard
                  scale={scale}
                  label="note-scale"
                  showLabelOnActiveOnly={showNotes.length > 0}
                  activeNotes={showNotes}
                  noteRange={{ firstNote: isSmallOrSmaller ? 'c4' : 'c3', lastNote: isSmallOrSmaller ? 'c5' : 'c6' }}
                  onPlayNote={info => {
                    const { note, octave } = getNoteAndOctave(info.note)
                    const noteEvent: NoteEvent = {
                      type: 'note',
                      octave,
                      note,
                      duration: '4n',
                    }
                    playNotes([noteEvent])
                  }}
                />
              </VBox>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default MusicTheory
